import { PRODUTOS, chaveProduto, produtoDoItem, type ChaveProduto } from "./mercado-produtos.js";

/**
 * O PAINEL do comprador — o Início v2.
 *
 * ═══ O QUE ESTA TELA É ══════════════════════════════════════════════════════
 *
 * O corretor faz tudo no papel e no WhatsApp. O painel tem de mostrar valor no
 * primeiro minuto, ANTES de ele ter cadastrado qualquer coisa, e virar a tela
 * dele com o uso. "Primeiro acesso" não é flag nem tela separada: é o mesmo
 * painel com dados escassos, cada bloco com seu vazio útil.
 *
 * ⚠️ NENHUM NÚMERO DE VENDA AQUI. Saíram do Início: A receber, saldo da semana,
 * margem e o fluxo de caixa. Gestão de venda é outra jornada, e um painel que
 * mistura "o que comprei" com "o que vou ganhar" vira relatório — que é
 * exatamente o que o corretor não abre no meio do pátio.
 *
 * ⚠️ TUDO AQUI É DERIVADO DOS DOCUMENTOS. `DocumentoDoComprador` é a espinha:
 * uma linha por romaneio (natureza 1) ou registro próprio (natureza 2), com a
 * DATA DO DOCUMENTO já resolvida pela regra do `pagar.ts` (data da venda para
 * romaneio, data de carregamento da carga para registro próprio). KPIs, compras
 * por semana, romaneios recentes, parceiros e o preço médio saem todos da MESMA
 * lista, em memória — não de cinco consultas com critérios próprios que
 * divergiriam no primeiro mês virado.
 */

// ─── A espinha ───────────────────────────────────────────────────────────────

export type NaturezaDocumento = "ROMANEIO" | "REGISTRO_PROPRIO";

/** Um produto dentro de um documento, já com kg e preço. */
export interface ItemDoDocumento {
  variedade: string | null;
  qualidade: string | null;
  kg: number;
  precoKg: number;
}

export interface DocumentoDoComprador {
  /** `compartilhamentoId` (natureza 1) ou `itemId` (natureza 2). */
  ref: string;
  natureza: NaturezaDocumento;
  /** "042/2026" no romaneio; o nº do bloco (ou "registro") no registro próprio. */
  numero: string;
  produtorContatoId: string | null;
  /** A org do produtor (natureza 1). `null` no registro próprio — lá não há org. */
  produtorOrgId: string | null;
  /**
   * QUEM entregou: o nome da carteira do comprador, ou o do usuário que
   * formalizou o romaneio. É a PESSOA, e é ela que o corretor conhece.
   */
  produtorNome: string;
  /**
   * A FAZENDA (a organização), quando ela tem nome próprio — "Fazenda Boa Vista".
   * `null` no registro próprio e quando o nome da pessoa já é o da org.
   *
   * ⚠️ São DOIS campos porque a tela precisa dos dois: "Hugo (Fazenda Boa Vista)"
   * na pendência, "Hugo" onde só cabe um nome. Um campo só obrigava a tela a
   * cortar a string, e cortar "Fazenda Boa Vista" no primeiro espaço produzia
   * "Fazenda" — que foi o que apareceu em produção.
   */
  produtorFazenda: string | null;
  /** Data do DOCUMENTO — a régua de "do mês" e "da semana". */
  data: string;
  /** kg conferido quando houve conferência; senão o do documento. */
  kg: number;
  /** Os kg como o DOCUMENTO os traz, antes da conferência — a base do "−120 na conf.". */
  kgEmitido: number;
  valor: number;
  vencimento: string | null;
  /** `true` quando existe conferência registrada (qualquer resultado). */
  conferido: boolean;
  /** `true` quando a conferência mudou os kg. */
  ajustado: boolean;
  cargaId: string | null;
  cargaIdentificador: string | null;
  /** EM_FORMACAO | FECHADA | ENTREGUE — a cor do chip da carga. `null` sem carga. */
  cargaStatus: string | null;
  itens: ItemDoDocumento[];

  // ─── O estado OPERACIONAL (a lista de Romaneios) ───────────────────────────
  //
  // ⚠️ Estes campos entram na MESMA espinha, e não numa segunda união: a lista
  // de Romaneios e o painel do Início respondem sobre os mesmos documentos, e
  // duas leituras com critérios próprios divergiriam no primeiro mês virado —
  // que é a razão de `documentosDoComprador` existir. O Início simplesmente não
  // lê o que não usa.

  /** O produtor tem conta no Rizoma — o selo verde. */
  noRizoma: boolean;
  /** O WhatsApp do produtor: a busca por número e o `wa.me` do detalhe. */
  telefone: string | null;
  /** Fazenda (nome fantasia da org) ou cidade do contato — o subtítulo do detalhe. */
  produtorDetalhe: string | null;
  /** A CIDADE do produtor — a praça de onde a fruta saiu. `null` quando não há. */
  produtorCidade: string | null;
  /** O status materializado do dinheiro (`A_PAGAR` | `INFORMADO` | `PAGO` | …). */
  statusPagamento: string;
  /** O texto da divergência que o produtor registrou, quando houver. */
  conferenciaObs: string | null;
  /** O `/p/` do registro próprio (natureza 2). `null` no romaneio — ele tem o `/v/`. */
  token: string | null;
}

/**
 * A capacidade de referência de um caminhão, em kg.
 *
 * ⚠️ UMA constante, em shared. "≈ 3 caminhões" é uma frase de conversa, não um
 * número contábil — mas ela não pode dizer 3 numa tela e 4 em outra, e é isso
 * que duas constantes garantem que aconteça.
 */
export const CAPACIDADE_REFERENCIA_KG = 24000;

// ─── Datas civis (a TZ já vem resolvida por quem chama) ──────────────────────

/** A segunda-feira da semana civil de `data`. */
export function segundaDaSemana(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  // getUTCDay: 0 = domingo. Segunda é o início, então domingo recua 6 dias.
  const recuo = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - recuo);
  return d.toISOString().slice(0, 10);
}

export function somarDias(data: string, dias: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** As 8 segundas que terminam na semana de `hoje`, da mais antiga para a atual. */
export function oitoSemanas(hoje: string): string[] {
  const atual = segundaDaSemana(hoje);
  const semanas: string[] = [];
  for (let i = 7; i >= 0; i--) semanas.push(somarDias(atual, -7 * i));
  return semanas;
}

/** O primeiro dia do mês de `data`. */
export function primeiroDoMes(data: string): string {
  return `${data.slice(0, 7)}-01`;
}

/**
 * O mesmo período do mês ANTERIOR: dia 1 até o mesmo dia do mês.
 *
 * ⚠️ O dia é limitado ao último do mês anterior. Sem isso, 31/03 compararia com
 * "31 de fevereiro" — que o Postgres recusa e o JavaScript transforma em março,
 * fazendo o delta comparar o mês com ele mesmo.
 */
export function mesmoPeriodoMesAnterior(hoje: string): { desde: string; ate: string } {
  const ano = Number(hoje.slice(0, 4));
  const mes = Number(hoje.slice(5, 7));
  const dia = Number(hoje.slice(8, 10));
  const anteriorAno = mes === 1 ? ano - 1 : ano;
  const anteriorMes = mes === 1 ? 12 : mes - 1;
  const ultimoDia = new Date(Date.UTC(anteriorAno, anteriorMes, 0)).getUTCDate();
  const diaFinal = Math.min(dia, ultimoDia);
  const mm = String(anteriorMes).padStart(2, "0");
  return {
    desde: `${anteriorAno}-${mm}-01`,
    ate: `${anteriorAno}-${mm}-${String(diaFinal).padStart(2, "0")}`,
  };
}

function dentro(data: string, desde: string, ate: string): boolean {
  return data >= desde && data <= ate;
}

// ─── §3.2 KPIs ───────────────────────────────────────────────────────────────

export interface KpiRomaneios {
  mes: number;
  semana: number;
  aConferir: number;
  /** `null` sem histórico no mês anterior — o chip some, não mostra "+0". */
  deltaMesmoPeriodoMesAnterior: number | null;
  porSemana: number[];
}

export interface KpiComprado {
  kgMes: number;
  porVariedade: { PRATA: number; NANICA: number };
  /** `null` quando não fecha um caminhão — a tela diz a frase, não "≈ 0". */
  caminhoesEquivalentes: number | null;
  /** `true` quando o mês não tem nenhum registro próprio. */
  somenteRizoma: boolean;
}

export interface KpiAPagar {
  total: number;
  nDocumentos: number;
  nProdutores: number;
  proximoVencimento: string | null;
  porSemana: number[];
}

export interface AvatarProdutor {
  iniciais: string;
  noRizoma: boolean;
}

export interface KpiProdutores {
  total: number;
  noRizoma: number;
  registroProprio: number;
  avatares: AvatarProdutor[];
}

export function contarRomaneios(
  docs: DocumentoDoComprador[],
  hoje: string,
  aConferir: number,
): KpiRomaneios {
  const inicioMes = primeiroDoMes(hoje);
  const anterior = mesmoPeriodoMesAnterior(hoje);
  const inicioSemana = segundaDaSemana(hoje);
  const fimSemana = somarDias(inicioSemana, 6);

  const mes = docs.filter(d => dentro(d.data, inicioMes, hoje)).length;
  const noAnterior = docs.filter(d => dentro(d.data, anterior.desde, anterior.ate)).length;
  const semana = docs.filter(d => dentro(d.data, inicioSemana, fimSemana)).length;

  const semanas = oitoSemanas(hoje);
  const porSemana = semanas.map(ini =>
    docs.filter(d => dentro(d.data, ini, somarDias(ini, 6))).length);

  return {
    mes,
    semana,
    aConferir,
    // Sem nada no mês anterior o delta não existe: "+4" contra zero não informa
    // tendência, informa que a conta começou.
    deltaMesmoPeriodoMesAnterior: noAnterior === 0 && mes === 0 ? null : (noAnterior === 0 ? null : mes - noAnterior),
    porSemana,
  };
}

/** A variedade do item, normalizada para os dois baldes da praça. */
function baldeDaVariedade(item: ItemDoDocumento): "PRATA" | "NANICA" | null {
  const v = (item.variedade ?? "").toUpperCase();
  if (v.indexOf("NANICA") >= 0) return "NANICA";
  if (v.indexOf("PRATA") >= 0) return "PRATA";
  return null;
}

export function comprado(docs: DocumentoDoComprador[], hoje: string): KpiComprado {
  const doMes = docs.filter(d => dentro(d.data, primeiroDoMes(hoje), hoje));
  let kgMes = 0;
  const porVariedade = { PRATA: 0, NANICA: 0 };
  for (const d of doMes) {
    kgMes += d.kg;
    for (const i of d.itens) {
      const balde = baldeDaVariedade(i);
      if (balde) porVariedade[balde] += i.kg;
    }
  }
  kgMes = Math.round(kgMes);
  porVariedade.PRATA = Math.round(porVariedade.PRATA);
  porVariedade.NANICA = Math.round(porVariedade.NANICA);
  // ⚠️ NULL ABAIXO DE UM CAMINHÃO, nunca "≈ 0 caminhões". Com 5.000 kg no mês o
  // arredondamento dava zero, e a linha dizia "≈ 0 caminhões" logo abaixo do
  // número que diz que ele comprou 5 t — a tela se desmentindo em duas linhas.
  const caminhoes = Math.round(kgMes / CAPACIDADE_REFERENCIA_KG);
  return {
    kgMes,
    porVariedade,
    caminhoesEquivalentes: caminhoes >= 1 ? caminhoes : null,
    somenteRizoma: doMes.length > 0 && doMes.every(d => d.natureza === "ROMANEIO"),
  };
}

// ─── §3.6 Compras por semana ─────────────────────────────────────────────────

export interface SemanaCompra {
  inicio: string;
  kgPrata: number;
  kgNanica: number;
  emCurso: boolean;
}

export function comprasPorSemana(docs: DocumentoDoComprador[], hoje: string): SemanaCompra[] {
  const atual = segundaDaSemana(hoje);
  return oitoSemanas(hoje).map(inicio => {
    const fim = somarDias(inicio, 6);
    let kgPrata = 0;
    let kgNanica = 0;
    for (const d of docs) {
      if (!dentro(d.data, inicio, fim)) continue;
      for (const i of d.itens) {
        const balde = baldeDaVariedade(i);
        if (balde === "PRATA") kgPrata += i.kg;
        else if (balde === "NANICA") kgNanica += i.kg;
      }
    }
    return {
      inicio,
      kgPrata: Math.round(kgPrata),
      kgNanica: Math.round(kgNanica),
      emCurso: inicio === atual,
    };
  });
}

// ─── §3.7 Romaneios recentes ─────────────────────────────────────────────────

export interface SituacaoRomaneio {
  tipo: "A_CONFERIR" | "CONFERIDO" | "AJUSTADO" | "SEM_VENCIMENTO" | "VENCE" | "CARGA";
  rotulo: string;
}

export interface RomaneioRecente {
  ref: string;
  numero: string;
  subNumero: string;
  /** Já formatado: "Hugo (Fazenda Boa Vista)". */
  produtor: string;
  /** O nome curto, para o cartão do celular onde não cabem os dois. */
  produtorCurto: string;
  origem: "RIZOMA" | "REGISTRO_PROPRIO";
  data: string;
  kg: number;
  valor: number;
  situacoes: SituacaoRomaneio[];
}

/** "sex 11/09" — o mesmo formato curto do resto do Mercado. */
const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

export function diaCurto(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  return `${DIAS_SEMANA[d.getUTCDay()]} ${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

/** Dias civis entre duas datas (positivo quando `ate` é depois). */
export function diasAte(de: string, ate: string): number {
  const a = Date.parse(`${de}T00:00:00Z`);
  const b = Date.parse(`${ate}T00:00:00Z`);
  return Math.round((b - a) / 86400000);
}

export function situacoesDoDocumento(d: DocumentoDoComprador, hoje: string): SituacaoRomaneio[] {
  const s: SituacaoRomaneio[] = [];
  // A ordem é a da leitura: primeiro o que exige ação, depois o contexto.
  if (d.natureza === "ROMANEIO" && !d.conferido) s.push({ tipo: "A_CONFERIR", rotulo: "A conferir" });
  if (d.conferido && !d.ajustado) s.push({ tipo: "CONFERIDO", rotulo: "Conferido" });
  if (d.ajustado) s.push({ tipo: "AJUSTADO", rotulo: "Ajustado" });
  if (!d.vencimento) s.push({ tipo: "SEM_VENCIMENTO", rotulo: "Sem vencimento" });
  else {
    const dias = diasAte(hoje, d.vencimento);
    // "Vence sex 11/09" num dia 21/09 é leitura errada: aquele prazo não está por
    // vir, já passou. Mesmo `tipo` (a urgência é a mesma, a cor também), outro
    // verbo — é o verbo que diz ao corretor se ele está atrasado.
    if (dias < 0) s.push({ tipo: "VENCE", rotulo: `Venceu ${diaCurto(d.vencimento)}` });
    else if (dias <= 3) s.push({ tipo: "VENCE", rotulo: `Vence ${diaCurto(d.vencimento)}` });
  }
  if (d.cargaIdentificador) s.push({ tipo: "CARGA", rotulo: d.cargaIdentificador });
  return s;
}

export function romaneiosRecentes(docs: DocumentoDoComprador[], hoje: string, limite = 5): RomaneioRecente[] {
  return docs
    .slice()
    .sort((a, b) => (a.data === b.data ? a.ref.localeCompare(b.ref) : (a.data < b.data ? 1 : -1)))
    .slice(0, limite)
    .map(d => ({
      ref: d.ref,
      numero: d.numero,
      // "nº do produtor" e não "nº do Fulano": o que a linha precisa dizer é que a
      // numeração é do OUTRO lado (o talão dele), e o nome já está na coluna ao
      // lado. Com o nome aqui, "Fazenda Boa Vista" virava "nº do Fazenda".
      subNumero: d.natureza === "ROMANEIO" ? "nº do produtor" : "registro próprio",
      produtor: nomeDoProdutor(d.produtorNome, d.produtorFazenda),
      produtorCurto: nomeCurtoDoProdutor(d.produtorNome, d.produtorFazenda),
      origem: d.natureza === "ROMANEIO" ? "RIZOMA" : "REGISTRO_PROPRIO",
      data: d.data,
      kg: Math.round(d.kg),
      valor: Math.round(d.valor * 100) / 100,
      situacoes: situacoesDoDocumento(d, hoje),
    }));
}

// ─── §3.8 Produtores parceiros ───────────────────────────────────────────────

export interface ProdutorParceiro {
  /** A chave da linha: o contato da carteira, ou a org do produtor sem contato. */
  contatoId: string;
  iniciais: string;
  /** A pessoa. */
  nome: string;
  /** A fazenda, quando tem nome próprio — o "(Fazenda Boa Vista)" da linha. */
  fazenda: string | null;
  noRizoma: boolean;
  kgMes: number;
  romaneiosMes: number;
}

/**
 * QUEM é o produtor de um romaneio do Rizoma: a PESSOA e a FAZENDA.
 *
 * A fazenda é a org (nome fantasia, senão razão social). A pessoa é, nesta
 * ordem: como o comprador a chama na carteira dele; quem formalizou do lado do
 * produtor; e, sem nenhum dos dois, a própria fazenda (a tela então mostra um
 * nome só).
 *
 * ⚠️ CONTATO COM O NOME DA ORG NÃO É PESSOA (Hugo, 24/09). A sincronização da
 * carteira (`sincronizarCarteira`) cria o contato com o nome da ORG, e esse
 * nome, mandando sobre o do usuário, fazia a tela dizer só "Fazenda Boa Vista"
 * onde devia dizer "Hugo (Fazenda Boa Vista)". Um apelido de verdade ("Zé do
 * Alto") continua mandando — é o nome pelo qual o comprador o conhece.
 */
export function identidadeDoProdutor(d: {
  orgNome: string;
  orgFantasia: string | null;
  contatoNome: string | null;
  usuarioNome: string | null;
}): { pessoa: string; fazenda: string | null } {
  const fazenda = d.orgFantasia?.trim() || d.orgNome.trim();
  const mesmo = (a: string, b: string | null | undefined) =>
    !!b && a.replace(/\s+/g, " ").trim().toLowerCase() === b.replace(/\s+/g, " ").trim().toLowerCase();
  const contato = d.contatoNome?.trim() || null;
  const contatoEhOrg = !!contato && (mesmo(contato, d.orgNome) || mesmo(contato, d.orgFantasia));
  const pessoa = (contato && !contatoEhOrg ? contato : null) || d.usuarioNome?.trim() || contato || fazenda;
  return { pessoa, fazenda: mesmo(pessoa, fazenda) ? null : fazenda };
}

/** O primeiro nome de uma PESSOA. Nunca chamar com nome de fazenda. */
export function primeiroNomeDePessoa(nome: string): string {
  return nome.trim().split(/\s+/)[0] || nome;
}

/**
 * "Hugo (Fazenda Boa Vista)" — como o produtor aparece quando há espaço.
 *
 * ⚠️ NUNCA CORTAR O NOME DA FAZENDA. Em produção a tela dizia "Fazenda" e
 * "enviado pelo Fazenda": era `primeiroNome("Fazenda Boa Vista")`. Pessoa tem
 * primeiro nome; organização não — o nome dela é inteiro ou não é.
 */
export function nomeDoProdutor(nome: string, fazenda: string | null): string {
  const f = fazenda?.trim();
  if (!f || f === nome.trim()) return nome.trim();
  return `${primeiroNomeDePessoa(nome)} (${f})`;
}

/** O nome onde só cabe um: o primeiro nome da pessoa, ou a fazenda INTEIRA. */
export function nomeCurtoDoProdutor(nome: string, fazenda: string | null): string {
  const n = nome.trim();
  const f = fazenda?.trim();
  if (f && f === n) return n;
  return f ? primeiroNomeDePessoa(n) : n;
}

export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/**
 * A carteira como ela é de VERDADE: os contatos cadastrados MAIS os produtores
 * que já entregaram.
 *
 * ⚠️ PRODUTOR DO RIZOMA NÃO PRECISA ESTAR NA CARTEIRA PARA SER PARCEIRO. Quem
 * formaliza um romaneio entra aqui pela org, mesmo sem contato cadastrado —
 * senão o painel mostrava "1 romaneio, 10,3 t compradas" ao lado de "0
 * produtores · sua carteira está vazia", que é a mesma tela se desmentindo. É
 * exatamente a promessa do card: "produtores entram aqui sozinhos quando
 * formalizam um romaneio para você".
 *
 * A chave é o contato quando existe (é ele que une as duas naturezas) e a org
 * quando não — e o contato que aponta para a org ABSORVE os romaneios dela, para
 * o mesmo produtor não aparecer duas vezes.
 */
export function produtoresParceiros(
  docs: DocumentoDoComprador[],
  contatos: {
    id: string; nome: string; cidade: string | null; noRizoma: boolean;
    cadastradoEm: string | null; orgProdutorId?: string | null;
  }[],
  hoje: string,
  limite = 5,
): { topo: ProdutorParceiro[]; outros: { n: number; kg: number }; semRizoma: number; total: number } {
  const inicioMes = primeiroDoMes(hoje);

  // org → contato da carteira, para o romaneio sem contato cair na linha certa.
  const contatoPorOrg: Record<string, string> = {};
  for (const c of contatos) if (c.orgProdutorId) contatoPorOrg[c.orgProdutorId] = c.id;

  const chaveDoDoc = (d: DocumentoDoComprador): string | null =>
    d.produtorContatoId
    ?? (d.produtorOrgId ? (contatoPorOrg[d.produtorOrgId] ?? d.produtorOrgId) : null);

  interface Acumulado {
    chave: string; nome: string; fazenda: string | null; noRizoma: boolean;
    kgMes: number; romaneiosMes: number; ultimo: string; cadastro: string;
  }
  const porChave: Record<string, Acumulado> = {};

  for (const c of contatos) {
    porChave[c.id] = {
      chave: c.id, nome: c.nome, fazenda: null, noRizoma: c.noRizoma,
      kgMes: 0, romaneiosMes: 0, ultimo: "", cadastro: c.cadastradoEm ?? "",
    };
  }

  for (const d of docs) {
    const chave = chaveDoDoc(d);
    if (!chave) continue;
    const acc = porChave[chave] ?? {
      chave, nome: d.produtorNome, fazenda: d.produtorFazenda,
      // Documento com org é romaneio do Rizoma — o produtor tem conta.
      noRizoma: d.produtorOrgId != null,
      kgMes: 0, romaneiosMes: 0, ultimo: "", cadastro: "",
    };
    // A fazenda vem do DOCUMENTO: a carteira guarda como o comprador chama a
    // pessoa, não a razão social.
    if (!acc.fazenda && d.produtorFazenda) acc.fazenda = d.produtorFazenda;
    if (d.produtorOrgId) acc.noRizoma = true;
    if (d.data > acc.ultimo) acc.ultimo = d.data;
    if (dentro(d.data, inicioMes, hoje)) {
      acc.kgMes += d.kg;
      acc.romaneiosMes += 1;
    }
    porChave[chave] = acc;
  }

  const linhas: ProdutorParceiro[] = Object.keys(porChave).map(chave => {
    const a = porChave[chave];
    return {
      contatoId: a.chave,
      iniciais: iniciaisDe(a.nome),
      nome: a.nome,
      fazenda: a.fazenda,
      noRizoma: a.noRizoma,
      kgMes: Math.round(a.kgMes),
      romaneiosMes: a.romaneiosMes,
    };
  });

  // Com kg no mês, a ordem é o kg. Sem nenhum kg, é o último DOCUMENTO — nunca a
  // data de cadastro, que punha na frente quem nunca entregou. Quem não tem
  // documento nenhum desempata pelo cadastro mais recente, e só então pelo nome.
  const temKg = linhas.some(l => l.kgMes > 0);
  linhas.sort((a, b) => {
    if (temKg && a.kgMes !== b.kgMes) return b.kgMes - a.kgMes;
    const ua = porChave[a.contatoId].ultimo;
    const ub = porChave[b.contatoId].ultimo;
    if (ua !== ub) return ua < ub ? 1 : -1;
    const ca = porChave[a.contatoId].cadastro;
    const cb = porChave[b.contatoId].cadastro;
    if (ca !== cb) return ca < cb ? 1 : -1;
    return a.nome.localeCompare(b.nome, "pt-BR");
  });

  const topo = linhas.slice(0, limite);
  const resto = linhas.slice(limite);
  return {
    topo,
    outros: { n: resto.length, kg: resto.reduce((s, l) => s + l.kgMes, 0) },
    semRizoma: linhas.filter(l => !l.noRizoma).length,
    total: linhas.length,
  };
}

// ─── §3.3 Preço médio por produto ────────────────────────────────────────────

/** O que o comprador pagou num produto dentro de um intervalo. */
export interface PagoNoIntervalo {
  kg: number;
  /** Σ(kg × preço) ÷ Σkg, arredondado ao centavo. */
  precoMedioKg: number;
}

/**
 * A média PONDERADA POR KG do que ele pagou entre `desde` e `ate` (data do
 * DOCUMENTO), por produto da praça — as duas naturezas, romaneio do Rizoma e
 * registro próprio, e o kg CONFERIDO quando houve conferência (é o que
 * `documentosDoComprador` já entrega em `itens`).
 *
 * É a conta ÚNICA do "o que você pagou": o "seu preço médio · set" do card de
 * Cotações e o "você 1,40" de cada semana da matriz passam por aqui.
 *
 * ⚠️ Ponderada, nunca média das médias: dois romaneios de 10.000 kg a R$ 1,40 e
 * 500 kg a R$ 1,90 dão R$ 1,42, não R$ 1,65 — e é o R$ 1,42 que se compara com
 * a placa. Item que não casa com um produto da praça fica de fora (Nanica
 * classificada, produto sem variedade) e é CONTADO em `itensNaoMapeados`:
 * entrar misturaria preços de coisas diferentes numa média só.
 */
export function pagoPorProdutoNoIntervalo(
  docs: DocumentoDoComprador[],
  desde: string,
  ate: string,
): { porProduto: Record<string, PagoNoIntervalo>; itensNaoMapeados: number } {
  const soma: Record<string, { kg: number; valor: number }> = {};
  let itensNaoMapeados = 0;
  for (const d of docs) {
    if (!dentro(d.data, desde, ate)) continue;
    for (const i of d.itens) {
      if (i.kg <= 0) continue;
      const produto = produtoDoItem({
        variedadeNome: i.variedade,
        classificacao: i.qualidade,
        produtoNome: null,
      });
      if (!produto) { itensNaoMapeados++; continue; }
      const chave = chaveProduto(produto);
      const acc = soma[chave] ?? { kg: 0, valor: 0 };
      acc.kg += i.kg;
      acc.valor += i.kg * i.precoKg;
      soma[chave] = acc;
    }
  }
  const porProduto: Record<string, PagoNoIntervalo> = {};
  for (const chave of Object.keys(soma)) {
    const { kg, valor } = soma[chave];
    if (kg > 0) porProduto[chave] = { kg, precoMedioKg: Math.round((valor / kg) * 100) / 100 };
  }
  return { porProduto, itensNaoMapeados };
}

/**
 * O preço médio do MÊS de `hoje`, por produto — o "seu preço médio · set".
 *
 * ⚠️ Fora do Início (decisão de 22/09/2026, #237): a placa do painel de operação
 * mostra A COTAÇÃO, e só. Quem mostra é a tela de Cotações do comprador.
 */
export function precoMedioPorProduto(
  docs: DocumentoDoComprador[],
  hoje: string,
): Record<string, number> {
  const { porProduto } = pagoPorProdutoNoIntervalo(docs, primeiroDoMes(hoje), hoje);
  const medias: Record<string, number> = {};
  for (const chave of Object.keys(porProduto)) medias[chave] = porProduto[chave].precoMedioKg;
  return medias;
}

/** Os produtos da praça, na ordem fixa do registry. */
export function produtosDaPraca(): ChaveProduto[] {
  return PRODUTOS.map(p => ({ variedade: p.variedade, qualidade: p.qualidade }));
}

// ─── §3.5 Cargas no painel ───────────────────────────────────────────────────

/**
 * A carga como o PAINEL a mostra.
 *
 * ⚠️ SEM NENHUM VALOR EM REAIS. Nem preço de venda, nem margem, nem saldo a
 * receber. O card responde "quanto falta para fechar o caminhão", que é a
 * decisão do dia; dinheiro de venda saiu do Início inteiro nesta versão, e um
 * campo sobrevivente aqui traria a pergunta de volta pela porta dos fundos.
 */
export interface CargaDoPainelDTO {
  id: string;
  identificador: string;
  status: string;
  dataCarregamento: string | null;
  destinoNome: string | null;
  kgLiquido: number;
  alvoKg: number | null;
  faltam: number | null;
  nProdutores: number;
  nRomaneios: number;
  produtores: AvatarProdutor[];
  placa: string | null;
}

// ─── §3.1 Onboarding ─────────────────────────────────────────────────────────

export type ChavePasso =
  | "ACESSO" | "PRIMEIRO_ROMANEIO" | "REGISTRO_PROPRIO" | "PRIMEIRA_CARGA" | "CONVITE";

export interface PassoDTO {
  chave: ChavePasso;
  feito: boolean;
  rotulo: string;
  sub: string;
  /** O botão do passo. Ausente quando feito. */
  acao?: { rotulo: string; href: string };
}

export interface OnboardingDTO {
  passos: PassoDTO[];
  feitos: number;
  total: number;
  /** Com os cinco feitos o bloco some — para sempre, sem flag no banco. */
  completo: boolean;
  /** O primeiro produtor que formalizou para ele, para a frase de boas-vindas. */
  primeiroProdutor: string | null;
}

// ─── §3.3 Cotação ────────────────────────────────────────────────────────────

export interface LinhaCotacaoPainel {
  produto: string;
  variedade: string;
  qualidade: string | null;
  precoKg: number;
  /**
   * Contra a última semana publicada NA MESMA BASE (a mesma regra do quadro e
   * da matriz — `anteriorNaMesmaBase`); `null` sem base. Arredondada a 0,1.
   */
  variacaoSemanaPct: number | null;
  /** A data da publicação comparada ("vs 31/08"); `null` sem base. */
  comparadoCom: string | null;
  /** O preço vigente no FECHAMENTO de cada uma das últimas 8 semanas (a atual até hoje). */
  serie8: number[];
  /** A placa de cada ponto de `serie8` — ponto de VENDA sai vazado e fora da linha. */
  serie8Referencial: string[];
  /**
   * O preço médio que o comprador pagou neste produto no mês — média PONDERADA
   * por kg dos romaneios dele. Só na tela de Cotações: no Início a chave NÃO
   * existe (decisão do #237 — o painel de operação mostra a placa, e só).
   */
  seuPrecoMedio?: number | null;
}

/** A frase do rodapé: o produto de mais kg no mês contra a praça de hoje. */
export interface DestaqueSeuPrecoDTO {
  produto: string;
  /** Sempre PERCENTUAL: só existe quando a placa de hoje é ao produtor. */
  comparacao: { tipo: "PERCENTUAL" | "ABSOLUTO"; valor: number };
}

export interface CotacaoPainelDTO {
  praca: { slug: string; nome: string } | null;
  publicadaEm: string | null;
  /** PRODUTOR | VENDA da publicação mais recente; `null` sem cotação. */
  referencial: string | null;
  fonte: string;
  linhas: LinhaCotacaoPainel[];
  /** "AAAA-MM" do "seu preço médio · set" — só na tela de Cotações. */
  mesSeuPreco?: string;
  /** `null` quando não há o que comparar (placa de venda, sem compra no mês). Só em Cotações. */
  destaqueSeuPreco?: DestaqueSeuPrecoDTO | null;
}

// ─── §3.4 Pendências ─────────────────────────────────────────────────────────

export type TipoPendencia =
  | "CONFERIR" | "CARGA_FALTA" | "VENCE" | "SEM_VENCIMENTO" | "EXCEDENTE" | "CONTESTADO";

export interface AcaoPendencia {
  /** `rotulo` é texto de tela; `tipo` é o que o cliente usa para decidir o ato. */
  tipo: "CONFERIR_OK" | "CONFERIR_DIVERGENCIA" | "CHAMADA" | "VER" | "VENCIMENTO";
  rotulo: string;
  href?: string;
  /** O alvo do ato (compartilhamento, carga ou item), quando há ato. */
  alvo?: string;
}

export interface PendenciaPainelDTO {
  tipo: TipoPendencia;
  /** warn | info | neutro — o ícone e a cor da lista. */
  tom: "warn" | "info" | "neutro";
  titulo: string;
  sub: string;
  acoes: AcaoPendencia[];
}

// ─── O painel inteiro ────────────────────────────────────────────────────────

export interface PainelInicioDTO {
  onboarding: OnboardingDTO;
  kpis: {
    romaneios: KpiRomaneios;
    comprado: KpiComprado;
    aPagar: KpiAPagar;
    produtores: KpiProdutores;
  };
  cotacao: CotacaoPainelDTO;
  pendencias: { itens: PendenciaPainelDTO[]; aMais: number };
  cargas: {
    itens: CargaDoPainelDTO[];
    entreguesNoMes: { n: number; kg: number };
    /**
     * Quantas cargas estão EM FORMAÇÃO — inclusive as vazias, que não aparecem
     * em `itens`.
     *
     * ⚠️ É o que decide o caminho do "Novo romaneio": com carga aberta ele
     * PERGUNTA em qual; sem nenhuma, cria a carga de hoje e vai direto ao
     * registro. A carga vazia abandonada é justamente a que ele deve reaproveitar
     * — contá-la aqui é o que evita criar a segunda carga vazia do dia.
     */
    emFormacao: number;
  };
  comprasPorSemana: SemanaCompra[];
  romaneios: {
    recentes: RomaneioRecente[];
    rodape: { mes: number; viaRizoma: number; registroProprio: number };
  };
  produtores: {
    topo: ProdutorParceiro[];
    outros: { n: number; kg: number };
    semRizoma: number;
    total: number;
  };
}
